import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import CustomButton from '../reusable/CustomButton';
import { Button, Card, Divider, Tab, Tabs } from '@mui/material';
import { useRef } from 'react';
import Comments, { AuthorTag } from '../reusable/Comments';
import { ArrowBackIosOutlined } from '@mui/icons-material';
import Searchable from './Searchable';
import Highlighter from 'react-highlight-words';
import type {
  FocusGroup,
  FocusGroupFile,
  SchoolFocusGroup,
  SchoolFocusGroupReport,
} from '../../services/focusGroupService';
import { formatDate, useSchoolFocusGroupReport, useSchoolFocusGroup } from '../../services/focusGroupService';
import Loader from '../reusable/Loader';
import ExportIcon from '../assets/export-icon.svg';
import { sharedTabStyles } from '../reusable/sharedTabStyles';
import { SummaryUpdatingAlert } from './SummaryStatusAlerts';

import './SchoolFocusGroups.css';

const SummaryAbsentView = () => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 40px 80px 40px',
      minHeight: '400px',
    }}
  >
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '60px',
        maxWidth: '900px',
        width: '100%',
      }}
    >
      <div style={{ flex: '0 0 auto' }}>
        <img
          src="/images/focus-group-report-summary-in-progress.png"
          alt="Summary in progress"
          style={{
            width: '250px',
            height: 'auto',
            display: 'block',
          }}
        />
      </div>
      <div style={{ flex: '1', textAlign: 'left' }}>
        <h2
          style={{
            fontSize: '26px',
            fontWeight: 'normal',
            color: '#000000',
            marginBottom: '16px',
            marginTop: '0',
            lineHeight: '1.3',
          }}
        >
          Your summary is on its way
        </h2>
        <p
          style={{
            fontSize: '16px',
            color: '#000000',
            lineHeight: '1.5',
            margin: '0',
            maxWidth: '450px',
          }}
        >
          The summary information is being generated. Please check back in <strong>about 30 minutes</strong> to view the
          updated content.
        </p>
      </div>
    </div>
  </div>
);

const SchoolSummaryTab = ({ schoolFocusGroupReport }: { schoolFocusGroupReport: SchoolFocusGroupReport }) => {
  const exportSection = () => {
    const section = document.getElementById('school-summary');
    if (section) {
      const sectionCopy = section.cloneNode(true);
      const heading = document.createElement('h1');
      heading.innerText = schoolFocusGroupReport.school_name;

      const container = document.createElement('div');
      container.appendChild(heading);
      container.appendChild(sectionCopy);

      const exportButton = Array.from(container.getElementsByTagName('button'))[0];
      exportButton.parentNode?.removeChild(exportButton);

      const sectionHtml = container.innerHTML;
      const link = document.createElement('a');
      link.setAttribute('download', `${schoolFocusGroupReport.school_name} - Focus group summary.docx`);
      link.setAttribute('href', 'data: text/doc;charset=utf-8,' + encodeURIComponent(sectionHtml));
      link.click();
    }
  };

  const { summary, status } = schoolFocusGroupReport;

  const tabHeader = (
    <div className="tab-header">
      <h2 className="regular">School Overview</h2>
      <CustomButton
        variant="secondary"
        size="medium"
        buttonText="Export"
        icon={<img src={ExportIcon} alt="export icon"></img>}
        iconPosition="start"
        buttonAction={exportSection}
      />
    </div>
  );

  if (!summary || summary.trim() === '') {
    return (
      <div id="school-summary">
        {tabHeader}
        <SummaryAbsentView />
      </div>
    );
  }

  const isRegenerating = status === 'PROCESSING';

  return (
    <div id="school-summary">
      {isRegenerating && (
        <div className="summary-alert">
          <SummaryUpdatingAlert />
        </div>
      )}
      {tabHeader}

      <div
        style={{
          whiteSpace: 'pre-wrap',
        }}
      >
        {schoolFocusGroupReport.summary}
      </div>
      {!isRegenerating && (
        <>
          <h2 className="regular">Student response trends and quotes</h2>
          <ol>
            {schoolFocusGroupReport.themes.map((t) => (
              <li key={t.id}>
                <h3 className="regular">{t.name}</h3>
                <h4>Response Trends/Themes</h4>
                <ul className="nested-list">
                  {t.takeaways.map((s) => (
                    <li key={s}>{s}</li>
                  ))}
                </ul>
                <h4>Notable student quotes</h4>
                <ul className="nested-list">
                  {t.quotes.map((q) => (
                    <li key={q.id}>
                      {q.text.includes('...') ? (
                        <span>"{q.text}"</span>
                      ) : (
                        <Link
                          className="link"
                          to={`/school-focus-group/${schoolFocusGroupReport.school_id}/${q.focus_group_id}?t=${encodeURIComponent(q.text)}`}
                          style={{ marginRight: '8px', fontWeight: 'normal' }}
                        >
                          "{q.text}"
                        </Link>
                      )}
                      <AuthorTag speakerId={q.speaker_id} shape="chip" />
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ol>
        </>
      )}
    </div>
  );
};

export const ToC = ({ files }: { files: FocusGroupFile[] }) => {
  // Only show when there are multiple transcripts or notes
  if (files.length <= 1) {
    return null;
  }

  return (
    <div>
      <h2>Table of Contents</h2>
      <ol>
        {files?.map((file, i) => (
          <li key={file.data_file_id}>
            <a href={`#transcript-${i}`}>Transcript {i + 1}</a>
          </li>
        ))}
        {files?.map((file, i) => (
          <li key={file.data_file_id}>
            <a href={`#note-${i}`}>Notes {i + 1}</a>
          </li>
        ))}
      </ol>
    </div>
  );
};

const FocusGroupTab = ({
  focusGroup,
  schoolFocusGroup,
}: {
  focusGroup: FocusGroup;
  schoolFocusGroup: SchoolFocusGroup;
}) => {
  const [searchParams] = useSearchParams();
  const t = searchParams.get('t');
  const targetRef = useRef<HTMLDivElement>(null);

  if (!focusGroup) {
    return <div>Focus group not found</div>;
  }

  return (
    <Searchable>
      {({ searchInput, search, highlightClassName }) => {
        return (
          <div>
            <div style={{ display: 'flex', alignItems: 'baseline' }}>
              <div style={{ flex: '1' }}>
                <h2>{formatDate(focusGroup.focus_group_datetime)}</h2>
              </div>
              {searchInput}
            </div>
            <ToC files={focusGroup.files} />
            <h3>Transcript</h3>
            <div ref={targetRef}>
              {focusGroup.files.map((file, i) => {
                return (
                  <div key={file.data_file_id}>
                    <div id={`transcript-${i}`}>
                      <Comments
                        highlightClassName={highlightClassName}
                        highlight={[t ? decodeURIComponent(t) : search]}
                        quotes={file.transcript}
                      />
                    </div>
                    {(file.notes ?? []).length > 0 && (
                      <>
                        <Divider />
                        <h3>Notes</h3>
                        {(file.notes ?? []).map((note) => (
                          <div key={note.id}>
                            <h4>
                              <Highlighter
                                highlightClassName={highlightClassName}
                                searchWords={[search]}
                                autoEscape={true}
                                textToHighlight={note.title}
                              />
                            </h4>
                            <div>
                              <Highlighter
                                highlightClassName={highlightClassName}
                                searchWords={[search]}
                                autoEscape={true}
                                textToHighlight={note.text}
                              />
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      }}
    </Searchable>
  );
};

const FocusGroupContainer = () => {
  const { schoolId, focusGroupName } = useParams();
  const navigate = useNavigate();

  const { schoolFocusGroupReport, loading: loadingSchoolFocusGroupReport } = useSchoolFocusGroupReport({
    schoolId: schoolId as string,
  });
  const { schoolFocusGroup, loading: loadingSchoolFocusGroup } = useSchoolFocusGroup({ schoolId: schoolId as string });

  if (loadingSchoolFocusGroupReport || loadingSchoolFocusGroup) {
    return <Loader />;
  }

  if (!schoolFocusGroupReport || !schoolFocusGroup) {
    return <div>School not found</div>;
  }

  const focusGroupIndex = schoolFocusGroup.focus_groups.findIndex((fg) => fg.name === focusGroupName);

  const tabs = (
    <Tabs
      aria-label="Focus group navigation tabs"
      sx={sharedTabStyles.tabsContainer}
      value={focusGroupName ? focusGroupIndex + 1 : 0}
      onChange={(_, t) => {
        if (t === 0) {
          navigate(`/school-focus-group/${schoolId}`);
        } else {
          navigate(`/school-focus-group/${schoolId}/${schoolFocusGroup.focus_groups[t - 1]?.name}`);
        }
      }}
    >
      <Tab
        label="Summary"
        sx={sharedTabStyles.tabButton}
        id="summary-tab"
        aria-controls="summary-tabpanel"
        tabIndex={0}
      />
      {schoolFocusGroup.focus_groups.map((focusGroup, i) => (
        <Tab
          key={i}
          label={focusGroup.name}
          sx={sharedTabStyles.tabButton}
          id={`focus-group-tab-${i}`}
          aria-controls={`focus-group-tabpanel-${i}`}
          tabIndex={i + 1}
        />
      ))}
    </Tabs>
  );

  return (
    <>
      <div className="main-content">
        <Card sx={{ borderRadius: '20px', lineHeight: '1.4' }}>
          <div style={{ padding: '24px' }}>
            <Button
              style={{ width: 100 }}
              href={'/school-focus-groups'}
              startIcon={<ArrowBackIosOutlined />}
              size="small"
            >
              Back
            </Button>
            <h1 className="regular card-heading">{schoolFocusGroupReport.school_name}</h1>
            <div className="card-tabs">{tabs}</div>
            {focusGroupName ? (
              <FocusGroupTab
                schoolFocusGroup={schoolFocusGroup}
                focusGroup={schoolFocusGroup.focus_groups[focusGroupIndex]}
              />
            ) : (
              <SchoolSummaryTab schoolFocusGroupReport={schoolFocusGroupReport} />
            )}
          </div>
        </Card>
      </div>
    </>
  );
};

export default FocusGroupContainer;
